import { isKiosk, isMobile, isProduction } from '../../../../utils/platform';
import { createInstance } from '@optimizely/optimizely-sdk';
import { getParsedLocale } from '../../../../utils/i18nUtils';
import { getRange } from '../../../../utils/queryUtils';
import { getAnalyticsConsentStatus } from '../../../../cookies/getCookieConsentLevel';
import { getMemoizedPromiseFactory } from '../../../../utils/getMemoizedPromiseFactory';
import { isDefined } from '../../../../utils/isDefined';
import { checkIsFeatureEnabled } from '../../checkIsFeatureEnabled';
import { getParamOverrides } from '../../getParamOverrides';
const SDK_KEY = isProduction() ? '73ANu7N6D94s8inWeguwy' : 'WMDQRZCGXdYUMtzRVU2Hj'; // cspell:disable-line
const TIMEOUT = 900;
/**
 * Generates a user ID.
 *
 * @returns The generated user ID.
 */
const generateUserId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
/**
 * Returns the user ID.
 *
 * @returns The user ID.
 */
const getUserId = () => {
    let userId = localStorage.getItem('optimizely.userId');
    if (!userId) {
        userId = generateUserId();
        localStorage.setItem('optimizely.userId', userId);
    }
    return userId;
};
/**
 * Returns the user attributes.
 *
 * @returns The user attributes.
 */
const getUserAttributes = () => {
    return {
        countryCode: getParsedLocale().countryCode.toLowerCase(),
        range: getRange(),
        environment: isProduction() ? 'production' : 'development',
        isKiosk: isKiosk(),
        isMobile: isMobile()
    };
};
/**
 * Returns the Optimizely experiments.
 *
 * @param optimizely Optimizely client.
 * @returns The Optimizely experiments.
 */
const getOptimizelyExperiments = (optimizely) => {
    const userId = getUserId();
    const userAttributes = getUserAttributes();
    const experiments = (optimizely.getEnabledFeatures(userId, userAttributes) || [])
        .map(name => {
        const variation = optimizely.getVariation(name, userId, userAttributes);
        if (!variation) {
            return null;
        }
        return {
            name: name,
            variation: variation
        };
    })
        .filter(isDefined);
    return experiments;
};
const getValidOptimizelyEvents = (optimizely) => {
    const datafileJSON = optimizely.getOptimizelyConfig()?.getDatafile();
    if (datafileJSON) {
        try {
            const dataFile = JSON.parse(datafileJSON);
            // The datafile contains all valid events, i.e events that are defined in Optimizely
            return new Set(dataFile?.events
                ?.map(event => event?.key)
                .filter(eventKey => typeof eventKey === 'string') ?? []);
        }
        catch (error) {
            /* eslint-disable-next-line no-console */
            console.error('Failed to parse Optimizely JSON datafile', error);
        }
    }
    else {
        /* eslint-disable-next-line no-console */
        console.error('Failed to retrieve Optimizely config datafile');
    }
    return new Set();
};
const _initOptimizelyABTestClient = async () => {
    const analyticsConsentStatus = await getAnalyticsConsentStatus();
    if (analyticsConsentStatus !== 'ANALYTICS ALLOWED') {
        return {
            success: false,
            error: new Error('Missing cookie consent for analytics')
        };
    }
    let client = createInstance({
        sdkKey: SDK_KEY,
        eventBatchSize: 10,
        eventFlushInterval: 1000
    });
    if (!client) {
        return {
            success: false,
            error: new Error('Failed to create Optimizely Client Instance')
        };
    }
    const readyResult = await client.onReady({ timeout: TIMEOUT });
    if (!readyResult.success) {
        return {
            success: false,
            error: new Error(`Optimizely client initialization unsuccessful, reason: ${readyResult.reason ?? ''}`)
        };
    }
    const experiments = getOptimizelyExperiments(client);
    const activeExperiment = experiments[0] ?? null;
    const userId = getUserId();
    const userAttributes = getUserAttributes();
    // We only allow one enabled experiment
    if (activeExperiment) {
        client.activate(activeExperiment.name, userId, userAttributes);
    }
    const validEvents = getValidOptimizelyEvents(client);
    const reportEvent = ({ eventKey, eventTags }) => {
        if (!client || !validEvents.has(eventKey)) {
            return;
        }
        client.track(eventKey, userId, userAttributes, ...(eventTags ? [eventTags] : []));
    };
    const isFeatureEnabled = (name, variation) => {
        if (!client) {
            return false;
        }
        return checkIsFeatureEnabled({
            name,
            variation,
            activeExperiment,
            getOverrides: getParamOverrides
        });
    };
    const close = () => {
        if (!client) {
            return;
        }
        client?.close();
        client = null;
    };
    const isDisconnected = () => !client;
    const getActiveExperiment = () => activeExperiment;
    return {
        success: true,
        client: { reportEvent, close, isFeatureEnabled, isDisconnected, getActiveExperiment }
    };
};
export const initOptimizelyABTestClient = getMemoizedPromiseFactory(_initOptimizelyABTestClient);
//# sourceMappingURL=OptimizelyABTestClient.js.map